import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const FeaturedCenters = () => {
  const [favoriteIds, setFavoriteIds] = useState(new Set());

  const featuredCenters = [
    {
      id: 1,
      name: "Sedona Energy Healing Center",
      location: "Sedona, Arizona",
      image: "/assets/images/sedona-center.jpg",
      rating: 4.9,
      reviewCount: 324,
      priceFrom: 280,
      type: "EES Center",
      amenities: ["EES Technology", "Crystal Healing", "Chakra Balancing", "Meditation Garden"],
      practitioner: "Dr. Sarah Moonwhisper",
      featured: true,
      description: "Experience profound energy healing in Sedona's powerful vortex locations."
    },
    {
      id: 2,
      name: "Tulum Wellness Sanctuary",
      location: "Tulum, Mexico",
      image: "/assets/images/tulum-sanctuary.jpg",
      rating: 4.8,
      reviewCount: 198,
      priceFrom: 320,
      type: "Meditation Retreat",
      amenities: ["Beachfront Location", "Cacao Ceremonies", "Sound Baths", "Yoga Shalas"],
      practitioner: "Maya Celestial",
      featured: true,
      description: "Reconnect with your inner wisdom in this pristine beachfront sanctuary."
    },
    {
      id: 3,
      name: "Mount Shasta Healing Arts",
      location: "Mount Shasta, California",
      image: "/assets/images/mount-shasta.jpg",
      rating: 4.9,
      reviewCount: 287,
      priceFrom: 240,
      type: "Crystal Therapy",
      amenities: ["Sacred Mountain Views", "Lemurian Crystals", "Energy Work", "Forest Bathing"],
      practitioner: "Jonathan Starseed",
      featured: true,
      description: "Harness the mystical energy of sacred Mount Shasta for deep healing."
    },
    {
      id: 4,
      name: "Big Sur Mindfulness Retreat",
      location: "Big Sur, California",
      image: "/assets/images/big-sur.jpg",
      rating: 4.7,
      reviewCount: 156,
      priceFrom: 380,
      type: "Spa Resort",
      amenities: ["Ocean Views", "Hot Springs", "Massage Therapy", "Meditation Trails"],
      practitioner: "Elena Pacific",
      featured: true,
      description: "Find peace and renewal overlooking the dramatic Pacific coastline."
    },
    {
      id: 5,
      name: "Rishikesh Spiritual Center",
      location: "Rishikesh, India",
      image: "/assets/images/rishikesh.jpg",
      rating: 5.0,
      reviewCount: 445,
      priceFrom: 150,
      type: "Yoga Retreat",
      amenities: ["Ganges River Views", "Ancient Traditions", "Pranayama", "Vedic Studies"],
      practitioner: "Swami Anandamaya",
      featured: true,
      description: "Immerse yourself in authentic yogic traditions by the sacred Ganges."
    },
    {
      id: 6,
      name: "Bali Healing Village",
      location: "Ubud, Indonesia",
      image: "/assets/images/ubud-healing.jpg",
      rating: 4.8,
      reviewCount: 267,
      priceFrom: 195,
      type: "Detox Center",
      amenities: ["Rice Terrace Views", "Balinese Healing", "Raw Food Cuisine", "Water Blessings"],
      practitioner: "Kadek Spiritual",
      featured: true,
      description: "Experience traditional Balinese healing in the heart of Ubud's jungle."
    }
  ];

  const toggleFavorite = (centerId) => {
    setFavoriteIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(centerId)) {
        newSet.delete(centerId);
      } else {
        newSet.add(centerId);
      }
      return newSet;
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
    >
      {featuredCenters.map((center) => (
        <motion.div
          key={center.id}
          variants={cardVariants}
          className="group bg-white rounded-xl shadow-elevation-2 hover:shadow-elevation-4
                     transition-all duration-300 ease-out overflow-hidden border border-border"
        >
          {/* Image Container */}
          <div className="relative h-64 overflow-hidden">
            <div className="w-full h-full bg-gradient-to-br from-background-light to-background-medium
                             flex items-center justify-center">
              <Icon name="Image" size={48} className="text-text-secondary" /> {/* Changed from blue-300 */}
            </div>

            {/* Favorite Button */}
            <button
              onClick={() => toggleFavorite(center.id)}
              className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm
                         rounded-full flex items-center justify-center shadow-elevation-2
                         hover:bg-white transition-all duration-200 micro-interaction"
            >
              <Icon
                name="Heart"
                size={18}
                className={`transition-colors duration-200 ${
                  favoriteIds.has(center.id)
                    ? 'text-error fill-current' // Using 'error' color for filled heart
                    : 'text-text-secondary hover:text-error-500' // Using a lighter 'error' shade for hover
                }`}
              />
            </button>

            {/* Type Badge */}
            <div className="absolute top-4 left-4">
              <span className="px-3 py-1 bg-primary text-white text-xs font-medium {/* Changed from blue-600 */}
                               rounded-full shadow-elevation-1">
                {center.type}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Header */}
            <div className="mb-4">
              <h3 className="text-xl font-semibold text-text-primary mb-2 font-heading
                             group-hover:text-primary transition-colors duration-200"> {/* Changed from blue-600 */}
                {center.name}
              </h3>
              <div className="flex items-center text-text-secondary text-sm mb-2">
                <Icon name="MapPin" size={14} className="mr-1" />
                {center.location}
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={14} className="text-yellow-400 fill-current" />
                  <span className="text-sm font-medium text-text-primary">{center.rating}</span>
                  <span className="text-sm text-text-secondary">({center.reviewCount})</span>
                </div>
                <div className="text-sm text-text-primary">
                  <span className="font-semibold">${center.priceFrom}</span>
                  <span className="text-text-secondary"> /night</span>
                </div>
              </div>
            </div>

            {/* Description */}
            <p className="text-sm text-text-secondary mb-4 line-clamp-2">
              {center.description}
            </p>

            {/* Practitioner */}
            <div className="flex items-center space-x-2 mb-4 text-sm">
              <div className="w-6 h-6 bg-gradient-to-r from-primary to-accent {/* Changed from blue-400 to teal-400 */}
                               rounded-full flex items-center justify-center">
                <span className="text-xs text-white font-medium">
                  {center.practitioner?.split(' ')[0]?.[0] || 'P'}
                </span>
              </div>
              <span className="text-text-secondary">
                Guided by <span className="text-text-primary font-medium">{center.practitioner}</span>
              </span>
            </div>

            {/* Amenities */}
            <div className="mb-4">
              <div className="flex flex-wrap gap-1">
                {center.amenities?.slice(0, 3)?.map((amenity, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-background-light text-primary text-xs rounded-md"
                  >
                    {amenity}
                  </span>
                ))}
                {center.amenities?.length > 3 && (
                  <span className="px-2 py-1 text-text-secondary text-xs">
                    +{center.amenities.length - 3} more
                  </span>
                )}
              </div>
            </div>

            {/* Action Button */}
            <Link
              to={`/wellness-center-details/${center.id}`}
              className="inline-flex items-center justify-center w-full px-4 py-3
                         bg-gradient-to-r from-primary to-secondary text-white font-medium {/* Changed from blue-600 to blue-700 */}
                         rounded-lg hover:from-secondary hover:to-primary {/* Changed from blue-700 to blue-800 */}
                         transition-all duration-200 ease-out micro-interaction"
            >
              View Details
              <Icon name="ArrowRight" size={16} className="ml-2" />
            </Link>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default FeaturedCenters;
